/* eslint-disable import/no-cycle */
import * as React from 'react';
import { EventUtilsOptOut } from '../../utils/ReactHelpers';
import { SliderRange } from './RangeSliderWidget';
import { SliderHandleContainer } from './SliderHandleContainer';
import { getSliderStyles } from './SliderStyles';
import { SliderTickBarWidget } from './SliderTickBarWidget';
import { SliderPositionRange, SliderTrackWidget } from './SliderTrackWidget';
import { SliderValueLogic } from './SliderValueLogic';
import { OpenEndedAt } from './SliderWidget';

export enum HandleType {
  Both = 'both',
  Left = 'left',
  Right = 'right'
}

interface IndicatorAndDataBarPositions {
  /**
   * Indicates the area of the slider that 'has data'.
   */
  dataBarPosition?: SliderPositionRange;

  /**
   * Indicates the area of the slider that, considering the current value of the slider handles, is 'in range'.
   * E.g. for a two handled slider this would be the area between the handles. For an open ended slider, to the
   * left, it would be everything from the handle to the left end.
   */
  indicatorBarPosition?: SliderPositionRange;
}

export type SliderHandleAriaAttributes = Pick<React.AriaAttributes,
  'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'aria-valuetext'>;

export type SliderAriaAttributes = {
  /** The aria attributes associated with the left handle of a SliderWidget */
  leftHandle: SliderHandleAriaAttributes,
  /** The aria attributes associated with the right handle of a SliderWidget */
  rightHandle?: SliderHandleAriaAttributes,
};

export interface SliderBaseProps {
  /**
   * Aria attributes to be used by assistive technology.
   * **NOTE**: either `ariaLabel` or `ariaAttributes` must be provided.
   */
  ariaAttributes?: SliderAriaAttributes;

  /**
   * The description of the Slider that will be read by assistive technology.
   * **NOTE**: either `ariaLabel` or `ariaAttributes` must be provided.
   * @deprecated the behavior of this prop is WRONG when used with multi-handle sliders; instead use `ariaAttributes`.
  */
  ariaLabel?: string;

  /** Set to true to disable the slider. The slider is enabled by default. */
  disabled?: boolean;

  /** Set to true to have the slider be discrete. The default is false. */
  isDiscrete?: boolean;

  /** The max value in the domain of the slider, inclusive. */
  max: number;

  /** The min value in the domain of the slider, inclusive. */
  min: number;

  /**
   * When the slider is updated, this is called with the new value. The value can either be a single number or a SliderRange.
   * NOTE: The consumer will need to update the 'value' prop passed to this component for the slider to be visually updated.
   */
  onChange?: (value: number | SliderRange) => void;

  /**
   * Triggered when the user has finished a handle interaction, i.e. when the pointer or value-changing key is released.
   * Useful for e.g. waiting until the user has settled on a new value before firing an expensive backend call. Otherwise
   * `onChange` is sufficient. (The updated value itself should always be watched via the `onChange` event.)
   */
  onHandleRelease?: () => void;

  /**
   * Use to apply a darkened slider track bar for the specified open ended at type. This indicates that the slider is
   * "open-ended" from the current value to either min or max.
   */
  openEndedAt?: OpenEndedAt;

  /** The style to apply to the outermost element of the slider. */
  style?: React.CSSProperties;

  /** Test id to be attached to the slider and its contents. */
  testId: string;

  /**
   * When specified, the portion of the track in this range will be darkened, to indicate that there is data there.
   * NOTE: The darkened portion must be within the range of the two handles to be displayed.
   */
  trackBarDataRange?: SliderRange;

  /** The current value of the slider. It should be in the range of [min, max]. */
  value: number | SliderRange;
}

export interface SliderState {
  areHandlesBeingDragged: boolean;
  isRangeHandleFocused: boolean;
}

/**
 * A horizontal slider widget with one or two handles.
 */
export class SliderBaseWidget extends React.Component<SliderBaseProps, SliderState> {
  private sliderValueLogic: SliderValueLogic;

  public static defaultProps: Pick<SliderBaseProps, 'ariaAttributes'> = {
    ariaAttributes: { leftHandle: undefined!, rightHandle: undefined },
  };

  public constructor(props: SliderBaseProps) {
    super(props);

    this.validateProps(props);

    this.state = { areHandlesBeingDragged: false, isRangeHandleFocused: false };
    this.sliderValueLogic = new SliderValueLogic(this.props.min, this.props.max, !!this.props.isDiscrete);
  }

  private validateProps(props: SliderBaseProps): void {
    // eslint-disable-next-line deprecation/deprecation
    const { ariaLabel, ariaAttributes, min, max, value } = props;

    if (!ariaLabel && !ariaAttributes) {
      throw new Error(`Either ariaAttributes or ariaLabel must be provided.`);
    }

    if (isNaN(min) || isNaN(max) ||
      (isSingleHandleSlider(value) && isNaN(value)) ||
      (!isSingleHandleSlider(value) && (isNaN(value.from) || isNaN(value.to)))) {
      throw new Error(`NaN is not a valid value for min, max, or value(s).`);
    }
  }

  private getTickCount = (): number => {
    // And now for a word on how the ticks and handle manage to render all aligned and the handle(s) snaps to ticks:
    //
    // * The way SliderValueLogic does snapping via Math.round, so the number of integers from min to max become the
    //   number of snap points and by the magic of math are evenly distributed horizontally.
    // * The tick rendering in SliderTickBarWidget evenly distributes ticks horizontally as well, and the number of
    //   ticks also equals the number of integers from min to max.
    // * In other words, there are the same number of snap spots and number of ticks, and both are evenly distributed
    //   horizontally.
    // * Finally, to get the ticks to perfectly line up with the handle(s) (and the press indicator on the handle) we
    //   just have to make sure that the horizontal areas for the ticks and the handle start at the same x position of
    //   the page and are the same width.  This is controlled by the styles defined in SliderStyles.
    return this.props.isDiscrete ? Math.floor(this.props.max) - Math.ceil(this.props.min) + 1 : 0;
  }

  private getIndicatorAndDataBarPositions = (leftHandlePosition: number, rightHandlePosition?: number): IndicatorAndDataBarPositions => {
    if (isSingleHandleSlider(this.props.value) && !this.props.isDiscrete) {
      if (this.props.openEndedAt === OpenEndedAt.Min) {
        return { indicatorBarPosition: { start: 0, end: leftHandlePosition } };
      } else if (this.props.openEndedAt === OpenEndedAt.Max) {
        return { indicatorBarPosition: { start: leftHandlePosition, end: 1 } };
      }
    } else if (!isSingleHandleSlider(this.props.value)) {
      let dataBarPosition: SliderPositionRange | undefined = undefined;
      if (this.props.trackBarDataRange) {
        const dataBarStartPosition = this.sliderValueLogic.calculateSliderPositionAsProportion(this.props.trackBarDataRange.from);
        const dataBarEndPosition = this.sliderValueLogic.calculateSliderPositionAsProportion(this.props.trackBarDataRange.to);
        dataBarPosition = {
          start: Math.max(leftHandlePosition, dataBarStartPosition),
          end: Math.min(rightHandlePosition!, dataBarEndPosition) || undefined,
        };
        if (dataBarPosition.start > rightHandlePosition!) {
          dataBarPosition = undefined;
        }
      }
      return { indicatorBarPosition: { start: leftHandlePosition, end: rightHandlePosition }, dataBarPosition };
    }

    return {};
  }

  /**
   * A convenience method to fire the onChange method from props for when a single value (i.e. handle) has changed.
   */
  private fireOnChangeForSingleValue = (handle: HandleType, newValue: number): void => {
    if (!this.props.onChange) { return; }

    if (isSingleHandleSlider(this.props.value)) {
      if (newValue !== this.props.value) {
        this.props.onChange(newValue);
      }
    } else {
      if (handle === HandleType.Left && newValue !== this.props.value.from) {
        this.props.onChange({ from: Math.min(newValue, this.props.value.to), to: this.props.value.to });
      } else if (handle === HandleType.Right && newValue !== this.props.value.to) {
        this.props.onChange({ from: this.props.value.from, to: Math.max(newValue, this.props.value.from) });
      }
    }
  }

  /**
   * A convenience method to fire the onChange method from props for when both values have changed (i.e. both handles).
   */
  private fireOnChangeForTwoValues = (newFromValue: number, newToValue: number): void => {
    if (!this.props.onChange || isSingleHandleSlider(this.props.value) || newFromValue > newToValue) {
      return;
    }
    this.props.onChange({ from: newFromValue, to: newToValue });
  }

  private handleHandleDragStart = (handle: HandleType, position: number): void => {
    this.setState({ areHandlesBeingDragged: true });
    const newValue = this.sliderValueLogic.calculateNewSliderValueFromProportion(position);
    this.fireOnChangeForSingleValue(handle, newValue);
  }

  private handleHandleDragMove = (handle: HandleType, position: number): void => {
    const newValue = this.sliderValueLogic.calculateNewSliderValueFromProportion(position);
    this.fireOnChangeForSingleValue(handle, newValue);
  }

  private handleBothHandleDragStart = (): void => {
    this.setState({ areHandlesBeingDragged: true });
  }

  private handleBothHandleDragMove = (leftHandlePosition: number, rightHandlePosition: number): void => {
    const newFromValue = this.sliderValueLogic.calculateNewSliderValueFromProportion(leftHandlePosition);
    const newToValue = this.sliderValueLogic.calculateNewSliderValueFromProportion(rightHandlePosition);
    this.fireOnChangeForTwoValues(newFromValue, newToValue);
  }

  private handleHandleRelease = (): void => {
    this.setState({ areHandlesBeingDragged: false });
  }

  private handleHandleKeyDown = (handle: HandleType, key: string): void => {
    this.setState({ areHandlesBeingDragged: true });
    if (handle === HandleType.Both) {
      if (!isSingleHandleSlider(this.props.value)) {
        const newSliderRangeValue = this.sliderValueLogic.calculateNewSliderRangeValueForKey(key, this.props.value);
        if (newSliderRangeValue) {
          this.fireOnChangeForTwoValues(newSliderRangeValue.from, newSliderRangeValue.to);
        }
      }
    } else {
      let oldValue;
      if (isSingleHandleSlider(this.props.value)) {
        oldValue = this.props.value;
      } else {
        oldValue = handle === HandleType.Left ? this.props.value.from : this.props.value.to;
      }
      const newValue = this.sliderValueLogic.calculateNewSliderValueForKey(key, oldValue);
      if (newValue !== undefined) {
        this.fireOnChangeForSingleValue(handle, newValue);
      }
    }
  }

  private handleRangeHandleFocus = (focus: boolean): void => {
    this.setState({ isRangeHandleFocused: focus });
  }

  public componentDidUpdate(prevProps: SliderBaseProps, prevState: SliderState): void {
    this.validateProps(this.props);

    const { isDiscrete, max, min, onHandleRelease } = this.props;

    if (isDiscrete !== prevProps.isDiscrete || min !== prevProps.min || max !== prevProps.max) {
      this.sliderValueLogic = new SliderValueLogic(min, max, !!isDiscrete);
      this.forceUpdate();
    }
    if (prevState.areHandlesBeingDragged && !this.state.areHandlesBeingDragged) {
      if (onHandleRelease) {
        onHandleRelease();
      } else if (prevProps.onHandleRelease) {
        prevProps.onHandleRelease();
      }
    }
  }

  private getSliderHandleAriaAttributes(value: number, handleType: HandleType): React.AriaAttributes {
    const ariaAttributes: React.AriaAttributes = {
      ...(handleType === HandleType.Right ? this.props.ariaAttributes!.rightHandle : this.props.ariaAttributes!.leftHandle),
      'aria-valuemax': this.props.isDiscrete ? Math.floor(this.props.max) : this.props.max,
      'aria-valuemin': this.props.isDiscrete ? Math.ceil(this.props.min) : this.props.min,
      'aria-valuenow': value,
    };

    /* eslint-disable deprecation/deprecation */
    if (!ariaAttributes['aria-label'] && this.props.ariaLabel) {
      // this prop is deprecated and its behaivor is *wrong* per WAI-ARIA spec -- this is just here to preserve the old prop behavior
      ariaAttributes['aria-label'] = this.props.ariaLabel;
    }
    /* eslint-enable deprecation/deprecation */

    return ariaAttributes;
  }

  public render(): JSX.Element {
    const styles = getSliderStyles();

    let leftHandlePosition = undefined;
    let leftHandleAriaAttributes = undefined;
    let rightHandlePosition = undefined;
    let rightHandleAriaAttributes = undefined;

    if (isSingleHandleSlider(this.props.value)) {
      leftHandlePosition = this.sliderValueLogic.calculateSliderPositionAsProportion(this.props.value);
      leftHandleAriaAttributes = this.getSliderHandleAriaAttributes(this.props.value, HandleType.Left);
    } else {
      leftHandlePosition = this.sliderValueLogic.calculateSliderPositionAsProportion(this.props.value.from);
      leftHandleAriaAttributes = this.getSliderHandleAriaAttributes(this.props.value.from, HandleType.Left);
      rightHandlePosition = this.sliderValueLogic.calculateSliderPositionAsProportion(this.props.value.to);
      rightHandleAriaAttributes = this.getSliderHandleAriaAttributes(this.props.value.to, HandleType.Right);
    }

    const tickCount = this.getTickCount();
    const indicatorAndDataBarPositions = this.getIndicatorAndDataBarPositions(leftHandlePosition, rightHandlePosition);

    return (
      <div data-tb-test-id={this.props.testId} style={{ ...styles.OuterContainer, ...this.props.style }} {...EventUtilsOptOut}>
        <div style={styles.TrackAndHandleContainer}>
          {/* If there is just one tick, that means the min and max are equal, which means the handle(s) will be
              hidden.  So there isn't any point in showing the tick bar. */}
          {tickCount > 1 && <SliderTickBarWidget testId={this.props.testId} tickCount={tickCount} />}
          <SliderTrackWidget
            dataBarPosition={indicatorAndDataBarPositions.dataBarPosition}
            disabled={this.props.disabled}
            handleIsBeingDragged={this.state.areHandlesBeingDragged}
            isRangeHandleFocused={this.state.isRangeHandleFocused}
            rangeIndicatorBarPosition={indicatorAndDataBarPositions.indicatorBarPosition}
            testId={this.props.testId}
          />
          <SliderHandleContainer
            disabled={this.props.disabled}
            handles={{ leftHandlePosition, rightHandlePosition }}
            handlesAriaAttributes={{ leftHandleAriaAttributes, rightHandleAriaAttributes }}
            onBothHandleDragMove={this.handleBothHandleDragMove}
            onBothHandleDragStart={this.handleBothHandleDragStart}
            onHandleDragMove={this.handleHandleDragMove}
            onHandleDragStart={this.handleHandleDragStart}
            onHandleKeyDown={this.handleHandleKeyDown}
            onHandleRelease={this.handleHandleRelease}
            onRangeHandleFocus={this.handleRangeHandleFocus}
            testId={this.props.testId}
          />
        </div>
      </div>);
  }
}

/**
 * If this is true, then the slider should have a single handle. Conversely, if false, it should have two handles.
 */
const isSingleHandleSlider = (value: number | SliderRange): value is number => {
  return typeof value === 'number';
};
